import React, { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import { TeamsTracking } from '@enview/interface/types/bills/TeamsTracking';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import WorkspaceSelectorContent from '../DropDowns/WorkspaceSelectorContent';
import ArrowDown from '../../../components/svg/ArrowDown';
import { TeamAPI, BillTriageAPI } from '../../../api';
import { getOrgForTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector from '../../../helpers/TeamHelper';
import { getWorkspaceColorClass } from '../../../helpers/WorkspaceColor';
import './WorkspacesColumnField.scss';

// Cap the number of dots rendered for the multi-workspace indicator so a bill in
// many workspaces doesn't blow out the cell width; the count label covers the rest.
const MAX_INDICATOR_DOTS = 3;

interface WorkspacesColumnFieldProps {
  billId: string;
  billWorkspaces?: Partial<TeamsTracking>[];
}

// The interactive Workspaces table-column cell: shows the bill's current
// workspace(s) (empty / single / multi-with-count) and opens the shared
// WorkspaceSelectorContent to add/remove workspaces on click.
const WorkspacesColumnField = (props: WorkspacesColumnFieldProps): ReactElement => {
  const { billId, billWorkspaces = [] } = props;

  // id/name are always populated at runtime (this is a Bookshelf relation result,
  // not a genuinely partial object) — the Partial<> comes from BillDetailed's type.
  const workspaces = billWorkspaces.filter(
    (workspace): workspace is TeamsTracking =>
      workspace.id !== undefined && workspace.name !== undefined,
  );
  const billWorkspaceIds = workspaces.map((workspace) => workspace.id);

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const [setWorkspaces] = BillTriageAPI.endpoints.setBillWorkspaces.useMutation();
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const [show, setShow] = useState(false);

  const handleToggle = (): void => {
    setShow((prevShow) => !prevShow);
  };

  const handleClose = (): void => {
    setShow(false);
  };

  const handleSave = (workspaceIds: number[]): void => {
    handleClose();
    if (!orgId) return;
    void setWorkspaces({ orgId, billId, teamIds: workspaceIds });
  };

  let trigger: ReactElement;
  if (!workspaces.length) {
    trigger = (
      <span className="wcf-trigger wcf-trigger-empty">
        <span className="wcf-empty-label">Add to workspace</span>
        <ArrowDown className="wcf-caret" />
      </span>
    );
  } else if (workspaces.length === 1) {
    const [workspace] = workspaces;
    trigger = (
      <span className="wcf-trigger">
        <span className={`wcf-dot ${getWorkspaceColorClass(workspace.id)}`} />
        <span className="wcf-name">{workspace.name}</span>
        <ArrowDown className="wcf-caret" />
      </span>
    );
  } else {
    trigger = (
      <span className="wcf-trigger">
        <span className="wcf-dot-stack">
          {workspaces.slice(0, MAX_INDICATOR_DOTS).map((workspace) => (
            <span
              className={`wcf-stacked-dot ${getWorkspaceColorClass(workspace.id)}`}
              key={workspace.id}
            />
          ))}
        </span>
        <span className="wcf-name">{workspaces.length} workspaces</span>
        <ArrowDown className="wcf-caret" />
      </span>
    );
  }

  return (
    <ConfirmActionPopover
      align="start"
      className="workspace-picker-popover"
      closePopup={handleClose}
      content={
        show ? (
          <WorkspaceSelectorContent
            billWorkspaceIds={billWorkspaceIds}
            onSave={handleSave}
            sectionLabel="WORKSPACES"
            workspaces={userWorkspaces}
          />
        ) : null
      }
      hideCloseIcon
      onClickOutside={handleClose}
      positions={['bottom', 'top', 'right', 'left']}
      show={show}
      showArrowContainer={false}
      target={trigger}
      toggleCallback={handleToggle}
    />
  );
};

export default WorkspacesColumnField;
